import React, { useState } from "react";
import { AiOutlineDown, AiOutlineUp } from "react-icons/ai";
import logoBlack from "../assets/logo-black.png";

const faqs = [
  {
    category: "Account Management",
    questions: [
      {
        question: "How do I create an account or log in?",
        answer:
          "You can download our App from the Apple App Store and Google Play Store, or visit our <a href='https://www.conquerlife.io' target='_blank' rel='noopener noreferrer' style='color: #5EA5ED; text-decoration: underline;'>Conquer Life</a> website. Click on the 'Join the Climb' button on our welcome page to start the sign-up process. If you already have an account, click on the 'Already Joined? Login' link under the Join the Climb button.",
      },
      {
        question: "How do I log in to my account?",
        answer:
          "Our app uses OTP (One-Time Password) based login for your security. Simply enter your registered email or phone number, and you'll receive a one-time password to access your account. No need to remember passwords!",
      },
      {
        question: "How do I delete my account?",
        answer:
          "Go to the Menu drawer at the top right corner of the App home page, then navigate to Settings → Delete Account.",
      },
    ],
  },
  {
    category: "Profile & Settings",
    questions: [
      {
        question: "How do I view my profile?",
        answer:
          "Click on your username at the top of the App home page to open your Conquer Life Passport and view your profile details.",
      },
      {
        question: "How do I edit my profile?",
        answer:
          "Open your Conquer Life Passport by clicking on your username at the top of the App home page. Once your profile is open, click the edit button in the top right corner to switch the entire profile page to edit mode. This allows you to update your profile avatar, bio, username, badges section, achievements section, and more.",
      },
      {
        question: "How do I update my username?",
        answer:
          "Open your Conquer Life Passport and click the edit button in the top right corner. You can update your username twice in 14 days. Please avoid using the same name as in your email, avoid using your phone number, avoid using cuss words, and avoid using your name or nicknames.",
      },
      {
        question: "What are achievements and shields?",
        answer:
          "In the achievements section of your profile, you can see the collected shields or trophies you've earned by using the <a href='https://www.conquerlife.io' target='_blank' rel='noopener noreferrer' style='color: #5EA5ED; text-decoration: underline;'>Conquer Life App</a>. When you sign up, you receive a Citizen Shield. Other shields will be revealed as you spend more time on the app and engage with the community.",
      },
    ],
  },
  {
    category: "Connecting with Others",
    questions: [
      {
        question: "What is Anthem Arcade?",
        answer:
          "Anthem Arcade is where you take the first step in connecting with other users. Each day, you'll receive a curated set of up to 28 suggested profiles based on shared interests and activity. Click on a user's avatar to view their entire profile, then choose to either start a conversation or view the next suggested profile. Once you start a chat, you'll need to wait for the other user to respond. They have 48 hours to reply, or the conversation will expire and be removed from your chat list. After both users have engaged, you'll have 8 days to build your connection and decide if you want to move the chat to the private chat space, where it remains available in your chat list.",
      },
      {
        question: "How do I access Anthem Arcade?",
        answer:
          "On the App home page, click on the pin right above the Anthem Arcade building, or expand the menu option at the bottom left corner and click on the disc icon.",
      },
      {
        question: "How do I report, hide, or block the user in Anthem Arcade?",
        answer:
          "Inside the Anthem Arcade chatroom, click on the username to view the other user's profile. At the bottom of the profile page, you'll find options to report, hide, or block the user.",
      },
      {
        question: "What is the Chat Space?",
        answer:
          "The Chat Space is where you can see all your private chats with users who you've connected with through Anthem Arcade and mutually decided to continue chatting with. Here, you can engage in private one-on-one conversations or create group chats to connect with multiple users at once.",
      },
      {
        question: "How do I access the Chat Space?",
        answer:
          "On the App home page, click on the pin attached to the Wrigley building, or expand the menu option at the bottom left corner and click on the two faces chatting icon.",
      },
      {
        question: "How do I start a private chat with another user?",
        answer:
          "To connect with other users, explore Anthem Arcade where you can discover new people and start conversations. You'll have 8 days to connect and build your network with users before confirming to continue chatting with them in the private Chat Space. This gives you time to see if you'd like to maintain an ongoing friendship with them.",
      },
      {
        question: "How do I start a group chat?",
        answer:
          "Navigate to the Chat Space and look for the option to create a new group chat. From there, you can select users from your connections to add to the group.",
      },
      {
        question:
          "Can I report, hide, or block the user from a private chat or group chat?",
        answer:
          "Yes, open the chat and click on the username to view the user's profile. At the bottom of the profile page, you'll find the options to report, hide, or block the user.",
      },
      {
        question: "What is Breakout?",
        answer:
          "Breakout is a space where you can create or be part of a public or private event. These are community gatherings where you can connect with others around shared interests or activities.",
      },
      {
        question: "How do I access Breakout events?",
        answer:
          "On the App home page, click on the pin present on the open field area, or expand the menu option at the bottom left corner and click on the users group icon.",
      },
      {
        question: "Where can I find Public Breakout Events?",
        answer:
          "You can search for public Breakout Events from the Breakout Hub by clicking on the search icon. Public Breakout Events are also provided as recommendations on the App home page under Trending Today.",
      },
      {
        question: "How do I join a Public Breakout Event?",
        answer:
          "There are two ways to join a public Breakout Event: First, you can search for the event by name in the Breakout Hub. Second, if you have a join code, paste the exact code in the search bar inside the search page and click on the result to expand it. You'll then see an option to join the event.",
      },
      {
        question: "How do I join a Private Breakout Event?",
        answer:
          "You can only join a private Breakout Event by searching for it using the exact join code. Paste the code in the search bar inside the search page, click on the result to expand it, and you'll see an option to join the event.",
      },
      {
        question: "How do I report or hide a Breakout Event?",
        answer:
          "If you have joined the Breakout Event, open the Event chat → Click on the Event name to open the Breakout Info page → At the bottom you will find the Report and Hide option.",
      },
      {
        question:
          "What happens when I report, hide, or block a user / group chat / Breakout Event?",
        answer:
          "When you report content, our App team gets notified and will review the matter. When you hide a user, group chat, or Breakout Event, you will no longer see it in your account.",
      },
    ],
  },
  {
    category: "App Features",
    questions: [
      {
        question: "How can I open my profile?",
        answer:
          "On the App home page, click on your username at the top to open your Conquer Life Passport and view your profile details.",
      },
      {
        question: "How can I open the notifications page?",
        answer:
          "Click on the bird icon seen on the App home page, or expand the menu option present at the bottom left side of the App home page and select the bird icon there.",
      },
      {
        question: "How can I open Breakout events?",
        answer:
          "On the App home page, click on the pin present on the open field area, or expand the menu option at the bottom left corner and click on the users group icon.",
      },
      {
        question: "How do I open Anthem Arcade?",
        answer:
          "On the App home page, click on the pin right above the Anthem Arcade building, or expand the menu option at the bottom left corner and click on the disc icon.",
      },
      {
        question: "How do I open the Chat Space?",
        answer:
          "On the App home page, click on the pin attached to the Wrigley building, or expand the menu option at the bottom left corner and click on the two faces chatting icon.",
      },
      {
        question: "What are Kindness Coins?",
        answer:
          "Exchange friendly appreciation with this all-encompassing currency called a Kindness Coin. Make it count—you can send Kindness Coins to spread positivity throughout the app!",
      },
      {
        question: "How can I share Kindness Coins?",
        answer:
          "You can share Kindness Coins (KC) with other users in Breakout Event chats, Anthem Arcade chats, private chats, and group chats. Simply select the user you want to send Kindness Coins to, and you'll see options to send them. You can choose to send 1 KC or 8 KC along with a reason for your appreciation.",
      },
      {
        question: "How do I get Kindness Coins?",
        answer:
          "Your account starts with an initial balance of Kindness Coins. You'll receive more Kindness Coins when other users send them to you within the app. The more kindness you receive, the more you can share!",
      },
      {
        question: "What is a Love Bag?",
        answer:
          "All the love you have spread and thanks you have given are kept track of in this bag in the form of Love Points (LP). Each time kindness has been shared, the bag grows!",
      },
      {
        question: "Where can I find my Love Points?",
        answer:
          "You can view your total Love Points collected within the app on the App home page at the top right, next to your username.",
      },
      {
        question: "Will my Love Points expire?",
        answer:
          "No, Love Points do not expire. They will continue to grow as you share Kindness Coins and spread positivity throughout the community.",
      },
    ],
  },
  {
    category: "Privacy & Security",
    questions: [
      {
        question: "What should I do if my account is hacked?",
        answer:
          "If you believe your account has been compromised, don't worry—we're here to help! Please visit our Restore Account page for steps on securing your account and regaining access.",
      },
      {
        question: "How is my personal data used and protected?",
        answer:
          "Your privacy is important to us! To learn more about how we use and protect your personal data, please check out our <a href='https://documents.conquerlife.io/privacy-policy' target='_blank' rel='noopener noreferrer' style='color: #5EA5ED; text-decoration: underline;'>Privacy Policy</a>.",
      },
      {
        question: "How do I enable two-factor authentication?",
        answer:
          "Two-factor authentication is not currently available but may be added as a feature in the future to provide additional security for your account. We'll keep our users informed of any new security features as they become available.",
      },
    ],
  },
  {
    category: "Troubleshooting",
    questions: [
      {
        question: "Why can't I log in to my account?",
        answer:
          "A. Please check if you're entering the correct email or phone number. Make sure you're receiving the OTP (One-Time Password) in your email or SMS and entering it correctly before it expires. Also, ensure you have a stable internet connection.\nB. If your account has been reported multiple times, it may be temporarily restricted while our team reviews the reports. We will reach out to you via the email or phone number you verified during the signup process.",
      },
      {
        question: "How do I report a bug or issue?",
        answer:
          "We'd love to hear from you! Expand the Menu option at the top right corner of the App home page, then navigate to Settings and select the Feedback option to report any bugs, issues, or feature requests. Your feedback helps us improve the app for everyone.",
      },
    ],
  },
  {
    category: "Billing & Subscriptions",
    questions: [
      {
        question: "Does Conquer Life App have subscription plans?",
        answer:
          "No, presently Conquer Life App does not have any subscription plans. We will keep our users informed if we add subscription plans in the future.",
      },
    ],
  },
  {
    category: "Safety & Moderation",
    questions: [
      {
        question: "How does Conquer Life moderate content?",
        answer:
          "Conquer Life uses a combination of automated systems and human moderators to review user-generated content, including profiles and messages. Our moderation team works to identify and take action on content that violates our Community Guidelines.",
      },
      {
        question: "How do I report a user or content?",
        answer:
          "You can report a user or inappropriate content directly within the app. In any chat, click on the user's username to view their profile, then use the Report option at the bottom of the profile page. You can also report content in Breakout Events and group chats. All reports are reviewed by our team in a timely manner, and appropriate enforcement actions are taken.",
      },
      {
        question: "Can I block another user?",
        answer:
          "Yes. You can block any user at any time by visiting their profile and selecting the Block option. Once blocked, the user will no longer be able to contact you or see your profile.",
      },
      {
        question: "Can I leave or end a chat at any time?",
        answer:
          "Yes. You can leave or end any chat at any time. In Anthem Arcade, you can view the next suggested profile or choose not to respond. In the Chat Space, you can hide or leave any private or group conversation whenever you wish.",
      },
      {
        question: "What happens after I submit a report?",
        answer:
          "Once you submit a report, our moderation team is notified and will review the reported content or user promptly. Depending on the severity of the violation, enforcement actions may include a warning, temporary restriction, or permanent ban of the offending account. We take all reports seriously and strive to respond in a timely manner.",
      },
      {
        question: "How can I contact Conquer Life support?",
        answer:
          "You can reach our support team by emailing info@conquerlife.io. You can also submit feedback or report issues directly within the app by navigating to Menu → Settings → Feedback.",
      },
    ],
  },
  {
    category: "App Support",
    questions: [
      {
        question: "What is Conquer Life?",
        answer:
          "The Conquer Life App is a privacy-focused social platform for people to share what makes them really them—and for safe, meaningful conversations to take place using usernames rather than real names. Conquer Life Games catalyze group interaction with laughter and play.",
      },
      {
        question: "How do I contact customer support?",
        answer:
          "We're here to help! You can reach out to us by emailing info@conquerlife.io. Or, if you're in the app, simply tap on the Menu in the top right corner, navigate to Settings, and select the Feedback option. Whether it's a bug, an issue, or a feature request, we'd love to hear from you!",
      },
      {
        question: "Where can I find the terms & conditions and privacy policy?",
        answer:
          "Here is our <a href='https://documents.conquerlife.io/terms-and-conditions' target='_blank' rel='noopener noreferrer' style='color: #5EA5ED; text-decoration: underline;'>Terms & Conditions</a> and <a href='https://documents.conquerlife.io/privacy-policy' target='_blank' rel='noopener noreferrer' style='color: #5EA5ED; text-decoration: underline;'>Privacy Policy</a> documents.",
      },
      {
        question: "How do I suggest new features or improvements?",
        answer:
          "You can suggest new features or improvements by expanding the Menu option at the top right corner of the App home page, navigating to Settings, and selecting the Feedback option. We'd love to hear your ideas!",
      },
      {
        question: "Are there any community guidelines I should follow?",
        answer:
          "Absolutely! To help keep our community safe and enjoyable for everyone, please take a moment to review our <a href='https://documents.conquerlife.io/community-guidelines' target='_blank' rel='noopener noreferrer' style='color: #5EA5ED; text-decoration: underline;'>Community Guidelines</a> page.",
      },
    ],
  },
];

const FAQ = () => {
  const [expandedCategory, setExpandedCategory] = useState(null);

  const toggleCategory = (index) => {
    setExpandedCategory(expandedCategory === index ? null : index);
  };

  return (
    <div style={styles.page}>
      {/* ✅ HEADER */}
      <div style={styles.header}>
        <img
          src={logoBlack}
          alt="Conquer Life Logo"
          style={styles.logo}
        />
        <h1 style={styles.headerTitle} className="text-big-heading">CONQUER LIFE</h1>
      </div>
      {/* ✅ CONTENT */}
      <div style={styles.container}>
        <h1 style={styles.heading} className="text-big-heading">Frequently Asked Questions</h1>
        {faqs.map((faq, index) => (
          <div key={index} style={styles.faqBox}>
            <button
              className="text-sub-heading"
              onClick={() => toggleCategory(index)}
              style={{
                ...styles.categoryButton,
                backgroundColor:
                  expandedCategory === index ? "#5EA5ED" : "#fff",
                color: expandedCategory === index ? "#fff" : "#333",
              }}
            >
              <span style={styles.category} className="text-heading">{faq.category}</span>
              {expandedCategory === index ? (
                <AiOutlineUp size={24} />
              ) : (
                <AiOutlineDown size={24} />
              )}
            </button>
            {expandedCategory === index && (
              <div style={styles.questionsWrapper}>
                {faq.questions.map((q, qIndex) => (
                  <div key={qIndex} style={styles.questionCard}>
                    <p style={styles.question} className="text-sub-heading">{q.question}</p>
                    <p
                      style={styles.answer}
                      className="text-body"
                      dangerouslySetInnerHTML={{ __html: q.answer }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const styles = {
  page: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  header: {
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid black",
    height: "60px",
    padding: "0 15px",
    boxSizing: "border-box",
  },
  logo: {
    width: "40px",
    height: "40px",
    marginRight: "15px",
  },
  headerTitle: {
    fontSize: "26px",
    fontWeight: "bold",
    color: "black",
    margin: 0,
  },
  container: {
    padding: "2rem",
  },
  heading: {
    textAlign: "center",
    fontSize: "2.5rem",
    color: "#333",
    marginBottom: "2rem",
  },
  faqBox: {
    margin: "1rem 0",
    border: "1px solid #5EA5ED",
    borderRadius: "25px",
    overflow: "hidden",
    boxShadow: "0 3px 6px rgba(0,0,0,0.1)",
  },
  categoryButton: {
    width: "100%",
    padding: "1rem",
    fontSize: "1.25rem",
    fontWeight: "bold",
    border: "none",
    textAlign: "left",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
  },
  category: {},
  questionsWrapper: {
    padding: "1rem",
    // backgroundColor: "#f9f9f9",
  },
  questionCard: {
    // backgroundColor: "#fff",
    padding: "1rem",
    borderRadius: "12px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
    border: "1px solid #e0e0e0",
    marginBottom: "1rem",
  },
  question: {
    fontWeight: "600",
    color: "#333",
    marginBottom: "0.5rem",
  },
  answer: {
    whiteSpace: "pre-wrap",
    color: "#333",
  },
};

export default FAQ;
